import React, { FC, useCallback, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DataTable from 'components/shared/DataTable';
import { Column } from 'components/shared/DataTable/DataTable';
import Grid from 'components/UI/Grid';
import Loading from 'components/UI/Loading/Loading';
import { Paper } from '@mui/material';
import { FilterMatchMode } from 'primereact/api';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { NotificationTemplate } from 'models/restTypes';
import { useStores } from 'stores/hooks/hooks';

const StyledGrid = styled(Grid)`
  display: flex;
  flex: unset;
  height: 100%;
  width: 100%;
  align-items: normal;
`;

const DataTableContainer = styled(Paper)`
  overflow: hidden;
  height: 100%;
  background-color: var(--surface-ground);
`;

const EditableCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-height: 1.5em;
  padding: 2px 6px;
  border: 1px solid transparent;
  border-radius: 3px;
  cursor: text;
  transition: border-color 0.15s, background 0.15s;
  border-bottom: 1px dotted var(--primary-color);

  &:hover {
    border-color: var(--primary-color);
    background: var(--surface-hover);
  }

  &::after {
    content: '✎';
    font-size: 0.75em;
    opacity: 0;
    color: var(--primary-color);
    flex-shrink: 0;
    transition: opacity 0.15s;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const InlineInput = styled.input`
  width: 100%;
  border: 1px solid var(--primary-color);
  border-radius: 3px;
  padding: 2px 4px;
  font-size: inherit;
  font-family: inherit;
  background: var(--surface-0);
  color: var(--text-color);
  outline: none;
  box-sizing: border-box;
`;

const getTemplateKey = (template: NotificationTemplate) =>
  `${template.notificationTypeId}-${template.templateName}-${template.language}-${template.channel}-${template.providerName}`;

const NotificationTemplateTable: FC<{
  templates: NotificationTemplate[];
  isLoading: boolean;
}> = observer(({ templates, isLoading }) => {
  const { notificationStore } = useStores();
  const { t } = useTranslation('staticData');

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editingRef = useRef<string | null>(null);
  const originalValueRef = useRef<string | null>(null);

  const startEditing = useCallback((e: React.MouseEvent, rowData: NotificationTemplate) => {
    e.stopPropagation();
    const key = getTemplateKey(rowData);
    editingRef.current = key;
    originalValueRef.current = rowData.providerTemplateName ?? null;
    setEditingKey(key);
    setEditValue(rowData.providerTemplateName ?? '');
  }, []);

  const cancelEditing = useCallback(() => {
    editingRef.current = null;
    originalValueRef.current = null;
    setEditingKey(null);
  }, []);

  const saveEdit = useCallback(
    async (rowData: NotificationTemplate, value: string) => {
      if (!editingRef.current) return;
      editingRef.current = null;
      setEditingKey(null);
      const newValue = value.trim() || null;
      if (newValue === originalValueRef.current) return;
      originalValueRef.current = null;
      await notificationStore.updateProviderTemplateName(rowData, newValue);
    },
    [notificationStore],
  );

  const [filters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    notificationTypeId: { value: '', matchMode: FilterMatchMode.CONTAINS },
    channel: { value: '', matchMode: FilterMatchMode.CONTAINS },
    providerName: { value: '', matchMode: FilterMatchMode.CONTAINS },
    templateName: { value: '', matchMode: FilterMatchMode.CONTAINS },
    language: { value: '', matchMode: FilterMatchMode.CONTAINS },
    templateId: { value: '', matchMode: FilterMatchMode.CONTAINS },
    templateSource: { value: '', matchMode: FilterMatchMode.CONTAINS },
    templateText: { value: '', matchMode: FilterMatchMode.CONTAINS },
    imageUri: { value: '', matchMode: FilterMatchMode.CONTAINS },
    providerTemplateName: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const onRowClick = useCallback(
    ({ data }: DataTableRowClickEvent) => {
      notificationStore.setSelectedTemplate(data as NotificationTemplate);
    },
    [notificationStore],
  );

  const templateTextBody = useCallback((rowData: NotificationTemplate) => {
    if (!rowData.templateText) return '-';
    return rowData.templateText.length > 50
      ? `${rowData.templateText.substring(0, 50)}...`
      : rowData.templateText;
  }, []);

  const imageUriBody = useCallback((rowData: NotificationTemplate) => {
    if (!rowData.imageUri) return '-';
    return (
      <a
        href={rowData.imageUri}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={rowData.imageUri}
        title={rowData.imageUri}
      >
        {rowData.imageUri}
      </a>
    );
  }, []);

  const providerTemplateNameBody = useCallback(
    (rowData: NotificationTemplate) => {
      if (editingKey === getTemplateKey(rowData)) {
        return (
          <InlineInput
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => saveEdit(rowData, editValue)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit(rowData, editValue);
              else if (e.key === 'Escape') cancelEditing();
            }}
            onClick={(e) => e.stopPropagation()}
          />
        );
      }

      return (
        <EditableCell onClick={(e) => startEditing(e, rowData)}>
          {rowData.providerTemplateName || '-'}
        </EditableCell>
      );
    },
    [editingKey, editValue, saveEdit, cancelEditing, startEditing],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StyledGrid flex={1}>
      <DataTableContainer elevation={3}>
        <DataTable
          id="NotificationTemplateTextTable"
          showGridlines
          resizableColumns
          value={templates}
          selectionMode="single"
          scrollable
          scrollHeight="flex"
          emptyMessage={t('empty_message')}
          stripedRows
          filters={filters}
          onRowClick={onRowClick}
          selection={notificationStore.selectedTemplate}
          filterDisplay="row"
          globalFilterFields={[
            'notificationTypeId',
            'channel',
            'providerName',
            'templateName',
            'language',
            'templateId',
            'templateSource',
            'templateText',
            'imageUri',
            'providerTemplateName',
          ]}
          paginator
          rows={10}
        >
          <Column
            field="notificationTypeId"
            header={t('notification_type_id')}
            sortable
            filter
          />
          <Column field="channel" header={t('channel')} sortable filter />
          <Column
            field="providerName"
            header={t('provider_name')}
            sortable
            filter
          />
          <Column
            field="templateName"
            header={t('template_name')}
            sortable
            filter
          />
          <Column field="language" header={t('language')} sortable filter />
          <Column
            field="templateId"
            header={t('template_id')}
            sortable
            filter
          />
          <Column
            field="templateSource"
            header={t('template_source')}
            sortable
            filter
          />
          <Column
            field="templateText"
            header={t('template_text')}
            sortable
            filter
            body={templateTextBody}
            style={{ maxWidth: '300px' }}
          />
          <Column
            field="imageUri"
            header={t('image_uri')}
            sortable
            filter
            body={imageUriBody}
          />
          <Column
            field="providerTemplateName"
            header={t('provider_template_name')}
            sortable
            filter
            body={providerTemplateNameBody}
          />
        </DataTable>
      </DataTableContainer>
    </StyledGrid>
  );
});

export default NotificationTemplateTable;
